import React from "react";
import { connect } from "react-redux";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { WhatsappShareButton, WhatsappIcon } from "react-share";
import QRCode from "react-qr-code";
import Flash from "../../../../../utility/Flash";
import { frontHost } from "../../../../../frontHost";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const COLORS = ["#a7cc48", "#4fb0c6", "#f4a261", "#e63946", "#9b5de5"];

function monthlyData(merchants) {
  const now = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return {
      month: d.toLocaleString("default", { month: "short" }),
      referrals: merchants.filter((m) => {
        const c = new Date(m.createdAt);
        return (
          c.getFullYear() === d.getFullYear() && c.getMonth() === d.getMonth()
        );
      }).length,
    };
  });
}

function planData(merchants) {
  const map = {};
  merchants.forEach((m) => {
    const p = m.plan || "Free";
    map[p] = (map[p] || 0) + 1;
  });
  return Object.entries(map).map(([name, value]) => ({ name, value }));
}

function StatCard({ label, value, icon, sub, accent }) {
  return (
    <div style={styles.card}>
      <div
        style={{
          ...styles.cardIcon,
          background: accent
            ? "rgba(167,204,72,0.15)"
            : "rgba(79,176,198,0.12)",
        }}
      >
        <i
          className={icon}
          style={{ fontSize: 20, color: accent ? PRIMARY : "#4fb0c6" }}
        />
      </div>
      <div>
        <div style={styles.cardValue}>{value ?? "—"}</div>
        <div style={styles.cardLabel}>{label}</div>
        {sub && <div style={styles.cardSub}>{sub}</div>}
      </div>
    </div>
  );
}

function Index({ profile, merchants }) {
  const allMerchants = merchants || [];
  const activeCount = allMerchants.filter(
    (m) => m.subscriptionEnd && new Date() < new Date(m.subscriptionEnd),
  ).length;

  let commission = 0;
  allMerchants.forEach((m) => {
    if (m.payment && m.payment.length > 0) {
      commission += parseInt(m.payment[0]?.price || 0, 10);
    }
  });

  const thisMonth = new Date();
  const thisMonthCount = allMerchants.filter((m) => {
    const c = new Date(m.createdAt);
    return (
      c.getFullYear() === thisMonth.getFullYear() &&
      c.getMonth() === thisMonth.getMonth()
    );
  }).length;

  const refLink = profile?.agentCode
    ? `${frontHost}/register?ref=${profile.agentCode}`
    : "";

  const approved = profile?.approve;
  const monthly = monthlyData(allMerchants);
  const plans = planData(allMerchants);

  return (
    <div style={styles.root}>
      {/* Hero */}
      <div style={styles.hero}>
        <div>
          <div style={styles.heroGreeting}>Welcome back,</div>
          <div style={styles.heroName}>
            {profile?.fullName || profile?.email || "Agent"}
          </div>
          <div style={styles.heroBadges}>
            <span
              style={{
                ...styles.badge,
                background: approved
                  ? "rgba(167,204,72,0.2)"
                  : "rgba(230,57,70,0.15)",
                color: approved ? "#5a8a00" : "#e63946",
              }}
            >
              <i
                className={approved ? "bi bi-check-circle-fill" : "bi bi-clock"}
                style={{ marginRight: 5 }}
              />
              {approved ? "Approved" : "Pending Approval"}
            </span>
            {profile?.sector > 0 && (
              <span style={styles.badge}>
                <i className='bi bi-geo-fill' style={{ marginRight: 5 }} />
                Sector {profile.sector}
              </span>
            )}
          </div>
        </div>
        {approved && profile?.agentCode && (
          <div style={styles.codeBox}>
            <div
              style={{
                fontSize: 11,
                color: "#aaa",
                marginBottom: 4,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Your Agent Code
            </div>
            <div style={styles.codeValue}>{profile.agentCode}</div>
          </div>
        )}
      </div>

      {/* Stat Cards */}
      <div style={styles.cardGrid}>
        <StatCard
          label='Total Merchants'
          value={allMerchants.length}
          icon='bi bi-shop-window'
          accent
        />
        <StatCard
          label='Active Subscriptions'
          value={activeCount}
          icon='bi bi-check2-circle'
        />
        <StatCard
          label='This Month'
          value={thisMonthCount}
          icon='bi bi-calendar-check'
        />
        <StatCard
          label='Commission Earned'
          value={"₦" + commission.toLocaleString()}
          icon='bi bi-cash-coin'
          accent
        />
      </div>

      {/* Charts */}
      <div style={styles.chartsRow}>
        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Monthly Merchant Referrals</div>
          <ResponsiveContainer width='100%' height={220}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
              <XAxis dataKey='month' tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis
                tick={{ fill: "#888", fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip />
              <Line
                type='monotone'
                dataKey='referrals'
                stroke={PRIMARY}
                strokeWidth={2}
                dot={{ fill: PRIMARY, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <div style={styles.chartTitle}>Merchant Plan Distribution</div>
          {plans.length > 0 ? (
            <ResponsiveContainer width='100%' height={220}>
              <PieChart>
                <Pie
                  data={plans}
                  cx='50%'
                  cy='50%'
                  outerRadius={75}
                  dataKey='value'
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {plans.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.empty}>
              <i
                className='bi bi-pie-chart'
                style={{ fontSize: 32, color: "#ccc" }}
              />
              <div style={{ color: "#aaa", marginTop: 8, fontSize: 13 }}>
                No data yet
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Referral Section */}
      {approved && (
        <div style={styles.referralCard}>
          <div style={styles.referralLeft}>
            <div style={styles.chartTitle}>
              <i
                className='bi bi-link-45deg'
                style={{ color: PRIMARY, marginRight: 6 }}
              />
              Your Referral Link
            </div>
            <div style={styles.refLinkBox}>{refLink}</div>
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 14,
                flexWrap: "wrap",
              }}
            >
              <CopyToClipboard
                text={refLink}
                onCopy={() =>
                  Flash("success", "Referral link copied!", "", 2500)
                }
              >
                <button style={styles.copyBtn}>
                  <i className='bi bi-clipboard' style={{ marginRight: 6 }} />
                  Copy Link
                </button>
              </CopyToClipboard>
              <CopyToClipboard
                text={profile?.agentCode}
                onCopy={() =>
                  Flash(
                    "success",
                    `Code copied: ${profile?.agentCode}`,
                    "",
                    2500,
                  )
                }
              >
                <button
                  style={{
                    ...styles.copyBtn,
                    background: "rgba(79,176,198,0.12)",
                    color: "#2980b9",
                    border: "1.5px solid #4fb0c6",
                  }}
                >
                  <i className='bi bi-hash' style={{ marginRight: 6 }} />
                  Copy Code
                </button>
              </CopyToClipboard>
              <WhatsappShareButton
                url={refLink}
                title='Join Excite Enterprise as a merchant via my referral!'
              >
                <button
                  style={{
                    ...styles.copyBtn,
                    background: "rgba(37,211,102,0.12)",
                    color: "#128C7E",
                    border: "1.5px solid #25d366",
                    cursor: "pointer",
                  }}
                >
                  <WhatsappIcon size={16} round style={{ marginRight: 6 }} />
                  WhatsApp
                </button>
              </WhatsappShareButton>
            </div>
          </div>
          <div style={styles.qrWrapper}>
            <QRCode value={refLink} size={100} />
            <div
              style={{
                fontSize: 10,
                color: "#aaa",
                marginTop: 6,
                textAlign: "center",
              }}
            >
              Scan to register
            </div>
          </div>
        </div>
      )}

      {!approved && (
        <div style={styles.pendingBanner}>
          <i
            className='bi bi-hourglass-split'
            style={{ fontSize: 28, color: "#f4a261", marginBottom: 10 }}
          />
          <div style={{ fontWeight: 600, color: BG }}>
            Account Pending Approval
          </div>
          <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Your referral code and link will be available once your account is
            approved.
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { padding: "0 0 40px 0" },
  hero: {
    background: BG,
    borderRadius: 12,
    padding: "24px 28px",
    marginBottom: 24,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
  },
  heroGreeting: {
    fontSize: 13,
    color: "rgba(255,255,255,0.5)",
    marginBottom: 4,
  },
  heroName: { fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 10 },
  heroBadges: { display: "flex", gap: 8, flexWrap: "wrap" },
  badge: {
    background: "rgba(255,255,255,0.1)",
    color: "rgba(255,255,255,0.7)",
    borderRadius: 20,
    padding: "3px 12px",
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
  },
  codeBox: {
    background: "rgba(255,255,255,0.08)",
    borderRadius: 10,
    padding: "12px 20px",
    textAlign: "center",
    border: "1.5px solid rgba(167,204,72,0.3)",
  },
  codeValue: {
    fontSize: 20,
    fontWeight: 800,
    color: PRIMARY,
    letterSpacing: 3,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },
  card: {
    background: "#fff",
    borderRadius: 10,
    padding: "18px 20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
  },
  cardIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardValue: { fontSize: 22, fontWeight: 700, color: BG, lineHeight: 1.2 },
  cardLabel: { fontSize: 12, color: "#888", marginTop: 2 },
  cardSub: { fontSize: 11, color: "#aaa", marginTop: 3 },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 20,
    marginBottom: 24,
  },
  chartCard: {
    background: "#fff",
    borderRadius: 10,
    padding: "20px 16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
  },
  chartTitle: { fontSize: 14, fontWeight: 600, color: BG, marginBottom: 14 },
  empty: {
    height: 180,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  referralCard: {
    background: "#fff",
    borderRadius: 10,
    padding: "24px 28px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 24,
    flexWrap: "wrap",
  },
  referralLeft: { flex: 1, minWidth: 240 },
  refLinkBox: {
    background: "#f8f8f8",
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 12,
    color: "#555",
    wordBreak: "break-all",
    marginTop: 10,
  },
  copyBtn: {
    background: "rgba(167,204,72,0.12)",
    color: "#5a8a00",
    border: "1.5px solid " + PRIMARY,
    borderRadius: 7,
    padding: "8px 16px",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
  },
  qrWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
  },
  pendingBanner: {
    background: "#fff",
    borderRadius: 10,
    padding: 40,
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    textAlign: "center",
  },
};

const mapStateToProps = (state) => ({
  profile: state.profile.userProfile,
  merchants: state.profile.agentMerchant,
});
export default connect(mapStateToProps)(Index);
